import React, { useState, useEffect } from 'react';
import { Calendar, AlertTriangle, Users, Paperclip } from 'lucide-react';
import CalendarTicketTitle from '../CalendarTicketTitle';
import TicketContextMenu from '../TicketContextMenu';
import TicketMergeModal from '../TicketMergeModal';
import { CalenderMarkersDisplay } from '../../utils/calenderMarkerUtils';
import { useI18n } from '../../utils/i18n';
import './ui.css';

// Gemeinsame Ticket-Karte (#138, W7) — Pflicht-Baustein für Karten-Listen,
// analog Modal/Toast/Combobox (Welle 2). Ersetzt die getrennten Karten-
// Implementierungen von TicketSidebar, DashboardTicketList und Glocke.
// Festes 4-Zeilen-Layout (Beschluss #121):
//   1. Kunden-Kurzform (fett, via CalendarTicketTitle)
//   2. Titel (einzeilig, Ellipsis)
//   3. Priorität + Status
//   4. Symbole (P/Kalender/Marker/Warndreieck) links, „vor X …" rechts
// Dazu: rote Aktivitäts-Ecke, „(n)"-Badge aus dem Titel-Suffix „… - (n)"
// (reine Anzeige, DB-Titel bleibt), optionale Kopf-/Fußzeile für Sonderfälle
// (Glocke: #Nr + Aktivitätstyp bzw. Zuweisungs-Zeile).
// Drag & Drop bleibt Sache des Aufrufers (Sidebar und Dashboard sprechen
// unterschiedliche Drag-Protokolle) — die Karte reicht die Handler nur durch.

export const PRIORITY_COLORS = {
  kritisch: 'var(--color-danger)',
  hoch: 'var(--color-orange)',
  normal: 'var(--color-primary)',
  niedrig: 'var(--color-success-alt)'
};

// Farbwerte = Stand der Sidebar (#121-Referenzlayout)
export const STATUS_COLORS = {
  offen: 'var(--color-danger)',
  in_bearbeitung: 'var(--color-orange)',
  wartend: 'var(--color-secondary)',
  zugewiesen: 'var(--color-info)',
  abgeschlossen: 'var(--color-success)'
};

export function isTicketOverdue(ticket) {
  if (!ticket || ticket.status === 'abgeschlossen' || ticket.isArchived) return false;
  if (!ticket.isPlanned || !ticket.plannedEndDateTime) return false;
  return new Date() > new Date(ticket.plannedEndDateTime);
}

export function ticketHasNewActivity(ticket, useAssigneePerspective = false) {
  if (!ticket) return false;
  if (useAssigneePerspective) {
    return !!(ticket.isNewTicketForAssignee || ticket.hasNewActivityForAssignee || ticket.hasNewEmailsForAssignee || ticket.hasNewCommentsForAssignee || ticket.hasNewAttachmentsForAssignee);
  }
  return !!(ticket.isNewTicket || ticket.hasNewActivity || ticket.hasNewEmails || ticket.hasNewComments || ticket.hasNewAttachments);
}

const TicketCard = ({
  ticket,
  onClick,
  onContextMenu,
  // #120 (W7): eingebautes einheitliches Kontextmenü (TicketContextMenu inkl.
  // Merge-Modal) — opt-in, damit Aufrufer mit eigenem Menü es nicht doppelt
  // bekommen. onTicketUpdate wird nach Menü-Aktionen gerufen.
  enableContextMenu = false,
  onTicketUpdate,
  useAssigneePerspective = false,
  hasNew: hasNewProp = undefined, // Ansicht-spezifische Ecke-Logik überschreibt den Default
  showAssignedTo = false,   // Zeile 4 links: „→ user" statt Symbolen (Pool-Spalte)
  headerRow = null,          // optionale Kopfzeile (Glocke: #Nr + Aktivitätstyp)
  footerRow = null,          // optionale Fußzeile (Glocke: Zuweisungs-Zeile)
  className = '',
  style = undefined,
  title = undefined,
  draggable = false,
  onDragStart,
  onDragEnd,
  dragging = false
}) => {
  const { t } = useI18n();

  const statusLabel = (status) => (
    status === 'offen' ? t('ticket.status.open', 'Offen') :
    status === 'in_bearbeitung' ? t('ticket.status.in_progress', 'In Bearbeitung') :
    status === 'wartend' ? t('ticket.status.waiting', 'Wartend') :
    status === 'zugewiesen' ? t('ticket.status.assigned', 'Zugewiesen') :
    status === 'abgeschlossen' ? t('ticket.status.completed', 'Abgeschlossen') : status
  );

  const priorityLabel = (priority) => (
    priority === 'kritisch' ? t('ticket.priority.critical', 'Kritisch') :
    priority === 'hoch' ? t('ticket.priority.high', 'Hoch') :
    priority === 'normal' ? t('ticket.priority.normal', 'Normal') :
    priority === 'niedrig' ? t('ticket.priority.low', 'Niedrig') : priority
  );

  const formatLastActivity = (lastActivityAt) => {
    if (!lastActivityAt) return null;
    const diffMs = Date.now() - new Date(lastActivityAt).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return t('ticketCard.justNow', 'Gerade eben');
    if (mins < 60) return t('ticketCard.minutesAgo', `vor ${mins} Min.`, { count: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('ticketCard.hoursAgo', `vor ${hours} Std.`, { count: hours });
    const days = Math.floor(hours / 24);
    return t('ticketCard.daysAgo', `vor ${days} Tg.`, { count: days });
  };

  // #120: internes Kontextmenü (nur wenn enableContextMenu)
  const [menuPosition, setMenuPosition] = useState(null);
  const [mergeSource, setMergeSource] = useState(null);

  useEffect(() => {
    if (!menuPosition) return undefined;
    const close = () => setMenuPosition(null);
    document.addEventListener('click', close);
    // #189: Rechtsklick feuert kein click — ohne contextmenu-Listener blieb
    // das Menü beim Rechtsklick auf eine andere Karte offen (Menü-Stapel).
    // Capture-Phase, damit das Schließen auch bei stopPropagation des
    // Ziel-Handlers greift; der öffnet sein eigenes Menü danach.
    document.addEventListener('contextmenu', close, true);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('contextmenu', close, true);
    };
  }, [menuPosition]);

  const handleContextMenu = (e, tk) => {
    if (onContextMenu) {
      onContextMenu(e, tk);
      return;
    }
    if (!enableContextMenu) return;
    e.preventDefault();
    e.stopPropagation();
    setMenuPosition({ x: e.clientX, y: e.clientY });
  };

  const hasNew = hasNewProp !== undefined ? !!hasNewProp : ticketHasNewActivity(ticket, useAssigneePerspective);
  const suffixMatch = /^(.*\S)\s*-\s*\((\d+)\)\s*$/.exec(ticket.title || '');
  const displayTicket = suffixMatch ? { ...ticket, title: suffixMatch[1] } : ticket;
  const isProjectTicket = !!((ticket.projectId && Number(ticket.projectId) > 0) || (ticket.subprojectId && Number(ticket.subprojectId) > 0));
  const overdue = isTicketOverdue(ticket);
  const lastActivity = formatLastActivity(ticket.lastActivityAt);
  const showIconsRow = isProjectTicket || ticket.isPlanned || ticket.hatAnhaenge || ticket.calenderMarker || overdue || lastActivity || (showAssignedTo && ticket.assignedTo);

  return (
    <div
      className={`ui-ticket-card ${dragging ? 'dragging' : ''} ${className}`.trim()}
      onClick={onClick ? (e) => onClick(e, ticket) : undefined}
      onContextMenu={(onContextMenu || enableContextMenu) ? (e) => handleContextMenu(e, ticket) : undefined}
      style={{ cursor: onClick ? 'pointer' : (draggable ? 'grab' : 'default'), ...style }}
      title={title !== undefined ? title : `${t('ticketDetail.ticket', 'Ticket')} #${ticket.ticketNumber || ticket.id}`}
      draggable={draggable || undefined}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      {hasNew && <div className="ui-ticket-card-corner" title={t('ticketCard.newActivity', 'Neue Aktivität')} />}
      {suffixMatch && (
        <div className="ui-ticket-card-seq-badge" title={t('ticketSidebar.seqBadge', 'Nummer im Vorgang')}>({suffixMatch[2]})</div>
      )}
      {headerRow}
      {/* #203: Ansprechpartner in der Kundenzeile — nur auf der Ticket-Karte */}
      <CalendarTicketTitle ticket={displayTicket} titleClassName="ui-ticket-card-title" showContact />

      <div className="ui-ticket-card-meta">
        <div className="ui-ticket-card-chip">
          <span className="ui-ticket-card-dot" style={{ backgroundColor: PRIORITY_COLORS[ticket.priority] || 'var(--color-secondary)' }} />
          <span>{priorityLabel(ticket.priority)}</span>
        </div>
        <div className="ui-ticket-card-chip">
          <span className="ui-ticket-card-dot" style={{ backgroundColor: STATUS_COLORS[ticket.status] || 'var(--color-secondary)' }} />
          <span>{statusLabel(ticket.status)}</span>
        </div>
      </div>

      {showIconsRow && (
        <div className="ui-ticket-card-icons">
          <div className="ui-ticket-card-icons-left">
            {isProjectTicket && (
              <span className="ui-ticket-card-project" title={t('ticketCard.projectTicket', 'Vorgangs-Ticket')}>P</span>
            )}
            {ticket.isPlanned && <Calendar size={12} className="text-blue-600" />}
            {/* #635: eigene Anhänge oder zählende Mail-Anhänge (Server: hatAnhaenge) */}
            {ticket.hatAnhaenge && (
              <span className="ui-ticket-card-anhang" role="img" aria-label={t('ticketCard.hasAttachments', 'Hat Anhänge')} title={t('ticketCard.hasAttachments', 'Hat Anhänge')}>
                <Paperclip size={12} aria-hidden="true" />
              </span>
            )}
            <CalenderMarkersDisplay markers={ticket.calenderMarker} size={12} />
            {overdue && (
              <AlertTriangle size={12} className="text-red-600" fill="red" title={t('ticket.overdueTitle', 'Ticket ist überfällig')} />
            )}
            {showAssignedTo && ticket.assignedTo && (
              <span className="ui-ticket-card-assigned">
                <Users size={12} /> → {ticket.assignedTo}
              </span>
            )}
          </div>
          {lastActivity && <span className="ui-ticket-card-age">{lastActivity}</span>}
        </div>
      )}
      {footerRow}
      {enableContextMenu && (menuPosition || mergeSource) && (
        // stopPropagation: Menü-/Modal-Klicks dürfen nicht als Karten-Klick
        // durchschlagen (Menü ist fixed positioniert, aber DOM-Kind der Karte)
        <div onClick={(e) => e.stopPropagation()} onContextMenu={(e) => e.stopPropagation()}>
          {menuPosition && (
            <TicketContextMenu
              ticket={ticket}
              position={menuPosition}
              onClose={() => setMenuPosition(null)}
              onTicketUpdate={onTicketUpdate}
              onOpenMerge={(tk) => setMergeSource(tk)}
            />
          )}
          {mergeSource && (
            <TicketMergeModal
              isOpen={true}
              sourceTicket={mergeSource}
              onClose={() => setMergeSource(null)}
              onMerged={onTicketUpdate}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default TicketCard;
